import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  TruckIcon, 
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { vehiclesService } from '../services/logistics/vehicles';
import { shipmentsService } from '../services/logistics/shipments';
import { truckTypesService } from '../services/logistics/truck-types';
import { odometerAutomationService } from '../services/logistics/odometer-automation';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import OdometerReadingModal from '../components/vehicles/OdometerReadingModal';
import OdometerHistoryModal from '../components/vehicles/OdometerHistoryModal';
import { toast } from 'react-hot-toast';

const VehicleDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentCompany, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [trips, setTrips] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Odometer modals state
  const [showOdometerModal, setShowOdometerModal] = useState(false);
  const [showOdometerHistory, setShowOdometerHistory] = useState(false);

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };

  const fetchVehicleAndTrips = async () => {
    if (!currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      // Load vehicle details, all shipments, and truck types in parallel
      const [vehicleData, shipmentsData, truckTypesData] = await Promise.all([
        vehiclesService.getVehicle(id),
        shipmentsService.getShipments(currentCompany.id),
        truckTypesService.getTruckTypes(currentCompany.id)
      ]);

      if (vehicleData && vehicleData.companyId === currentCompany.id) {
        setVehicle(vehicleData);
        setTruckTypes(truckTypesData);
        
        // Filter shipments for this vehicle
        const vehicleTrips = shipmentsData.filter(shipment => {
          // Check if shipment is assigned to this vehicle
          return shipment.vehicleId === id || 
                 shipment.vehicleId === parseInt(id) ||
                 shipment.vehiclePlate === vehicleData.plateNumber;
        });

        // Sort trips by creation date (newest first)
        const sortedTrips = vehicleTrips.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        });

        setTrips(sortedTrips);
        setFilteredTrips(sortedTrips);
      } else {
        setVehicle(null);
      }
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
      toast.error(t('vehicles.failed_to_load_details', 'Failed to load vehicle details'));
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleAndTrips();
  }, [id, currentCompany?.id, t]);

  // Auto-sync odometer when vehicle data is loaded
  useEffect(() => {
    if (vehicle && currentCompany?.id && user?.uid) {
      console.log(`🔄 Triggering auto-sync for vehicle ${vehicle.plateNumber}`);
      setTimeout(() => autoSyncOdometerFromFuel(), 1000);
    }
  }, [vehicle, currentCompany?.id, user?.uid]);

  // Filter trips based on status
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTrips(trips);
    } else {
      setFilteredTrips(trips.filter(trip => trip.status === statusFilter));
    }
  }, [statusFilter, trips]);

  // Odometer handlers
  const handleOdometerReadingAdded = () => {
    // Refresh vehicle data to get updated odometer information
    fetchVehicleAndTrips();
  };

  // Auto-sync odometer from fuel data when vehicle data loads
  const autoSyncOdometerFromFuel = async () => {
    console.log('🔍 Auto-sync check:', { 
      hasVehicle: !!vehicle, 
      hasCompany: !!currentCompany?.id, 
      hasUser: !!user?.uid,
      vehiclePlate: vehicle?.plateNumber 
    });
    
    if (!vehicle || !currentCompany?.id || !user?.uid) {
      console.log('❌ Auto-sync skipped - missing required data');
      return;
    }
    
    try {
      console.log(`🔄 Checking sync status for vehicle ${vehicle.plateNumber}...`);
      const status = await odometerAutomationService.getOdometerSyncStatus(currentCompany.id, { vehicleId: vehicle.id });
      
      console.log(`📊 Sync status:`, status);
      
      if (status.needsSync) {
        console.log(`🔄 Auto-syncing ${status.pending} odometer readings for vehicle ${vehicle.plateNumber}`);
        const result = await odometerAutomationService.syncOdometerFromMappedFuel(currentCompany.id, { vehicleId: vehicle.id, userId: user?.uid });
        
        console.log(`✅ Auto-sync result:`, result);
        
        if (result.created > 0) {
          console.log(`✅ Auto-synced ${result.created} odometer readings for vehicle ${vehicle.plateNumber}`);
          // Refresh vehicle data to show updated readings
          setTimeout(() => fetchVehicleAndTrips(), 1000);
        }
      } else {
        console.log(`ℹ️ No sync needed for vehicle ${vehicle.plateNumber} - already up to date`);
      }
    } catch (error) {
      console.error('❌ Error auto-syncing odometer from fuel:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      in_transit: { class: 'bg-blue-100 text-blue-800', icon: TruckIcon },
      delivered: { class: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      canceled: { class: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
        <Icon className="w-3 h-3 mr-1" />
        {t(`shipments.${status}`)}
      </span>
    );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return t('common.not_set');
    
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    // Format as DD/MM/YYYY in Georgian format
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getExpirationStatus = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', days: Math.abs(daysUntilExpiry), color: 'text-red-600' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', days: daysUntilExpiry, color: 'text-yellow-600' };
    } else {
      return { status: 'valid', days: daysUntilExpiry, color: 'text-green-600' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('vehicles.vehicle_not_found', 'Vehicle not found')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('vehicles.vehicle_not_found_message', `The vehicle with ID "${id}" could not be found.`)}
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/vehicles')}
            className="btn-primary"
          >
            {t('vehicles.back_to_vehicles', 'Back to Vehicles')}
          </button>
        </div>
      </div>
    );
  }

  const insuranceStatus = getExpirationStatus(vehicle.insuranceExpiry);
  const registrationStatus = getExpirationStatus(vehicle.registrationExpiry);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <button
            onClick={() => navigate('/vehicles')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {vehicle.plateNumber}
            </h1>
            <p className="text-sm text-gray-500">
              {vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 
            vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {t(`vehicles.${vehicle.status}`)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <TruckIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('vehicles.vehicle_information')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">{t('vehicles.plate_number')}</label>
                <p className="text-lg font-semibold text-gray-900">{vehicle.plateNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">{t('vehicles.make_model')}</label>
                <p className="text-gray-900">{vehicle.make} {vehicle.model}</p>
              </div>
              {vehicle.year && (
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('vehicles.year')}</label>
                  <p className="text-gray-900">{vehicle.year}</p>
                </div>
              )}
              {(vehicle.truckType || vehicle.type) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('vehicles.type')}</label>
                  <p className="text-gray-900">{getTruckTypeName(vehicle.truckType || vehicle.type)}</p>
                </div>
              )}
              {vehicle.capacity && (
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('vehicles.capacity')}</label>
                  <p className="text-gray-900">{vehicle.capacity}</p>
                </div>
              )}
              {vehicle.color && (
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('vehicles.color')}</label>
                  <p className="text-gray-900">{vehicle.color}</p>
                </div>
              )}
            </div>
          </div>

          {/* Odometer Information */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('vehicles.odometer_information', 'Odometer Information')}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowOdometerModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('vehicles.add_reading', 'Add Reading')}
                  </button>
                  <button
                    onClick={() => setShowOdometerHistory(true)}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    {t('vehicles.view_history', 'View History')}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body space-y-4">
              {vehicle.latestOdometerReading ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t('vehicles.current_reading', 'Current Reading')}
                    </label>
                    <button
                      onClick={() => setShowOdometerHistory(true)}
                      className="block text-2xl font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                      title="Click to view odometer history"
                    >
                      {vehicle.latestOdometerReading.reading.toLocaleString()} km
                    </button>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t('vehicles.last_updated', 'Last Updated')}
                    </label>
                    <button
                      onClick={() => setShowOdometerHistory(true)}
                      className="block text-gray-900 hover:text-blue-600 transition-colors cursor-pointer"
                      title="Click to view odometer history"
                    >
                      {formatDate(vehicle.latestOdometerReading.recordedAt)}
                    </button>
                  </div>
                  {vehicle.odometerStats && (
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('vehicles.total_distance', 'Total Distance')}
                        </label>
                        <p className="text-lg font-semibold text-green-600">
                          {vehicle.odometerStats.totalDistance.toLocaleString()} km
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          {t('vehicles.total_readings', 'Total Readings')}
                        </label>
                        <p className="text-lg font-semibold text-purple-600">
                          {vehicle.odometerStats.totalReadings}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-6">
                  <ClockIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="text-gray-500 mt-2">
                    {t('vehicles.no_odometer_readings', 'No odometer readings recorded')}
                  </p>
                  <button
                    onClick={() => setShowOdometerModal(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('vehicles.add_first_reading', 'Add first reading')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Document Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('vehicles.document_status')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              {/* Insurance */}
              <div>
                <label className="text-sm font-medium text-gray-600">{t('vehicles.insurance')}</label>
                {vehicle.insuranceExpiry ? (
                  <div className={`text-sm ${insuranceStatus?.color}`}>
                    <p className="font-medium">
                      {formatDate(vehicle.insuranceExpiry)}
                    </p>
                    <p className="text-xs">
                      {insuranceStatus?.status === 'expired' 
                        ? t('common.expired_days_ago', { days: insuranceStatus.days })
                        : insuranceStatus?.status === 'expiring'
                        ? t('common.expires_in_days', { days: insuranceStatus.days })
                        : t('common.valid_days', { days: insuranceStatus.days })
                      }
                    </p>
                    {vehicle.insuranceProvider && (
                      <p className="text-xs text-gray-500">{vehicle.insuranceProvider}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400">{t('common.not_set')}</p>
                )}
              </div>

              {/* Registration */}
              <div>
                <label className="text-sm font-medium text-gray-600">{t('vehicles.registration')}</label>
                {vehicle.registrationExpiry ? (
                  <div className={`text-sm ${registrationStatus?.color}`}>
                    <p className="font-medium">
                      {formatDate(vehicle.registrationExpiry)}
                    </p>
                    <p className="text-xs">
                      {registrationStatus?.status === 'expired' 
                        ? t('common.expired_days_ago', { days: registrationStatus.days })
                        : registrationStatus?.status === 'expiring'
                        ? t('common.expires_in_days', { days: registrationStatus.days })
                        : t('common.valid_days', { days: registrationStatus.days })
                      }
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-400">{t('common.not_set')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Trip Statistics */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">{t('vehicles.trip_statistics')}</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{trips.length}</p>
                  <p className="text-sm text-gray-600">{t('vehicles.total_trips')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {trips.filter(trip => trip.status === 'delivered').length}
                  </p>
                  <p className="text-sm text-gray-600">{t('vehicles.completed_trips')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {trips.filter(trip => trip.status === 'in_transit').length}
                  </p>
                  <p className="text-sm text-gray-600">{t('vehicles.active_trips')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {trips.filter(trip => trip.status === 'pending').length}
                  </p>
                  <p className="text-sm text-gray-600">{t('vehicles.pending_trips')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trips List */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('vehicles.vehicle_trips')}</h3>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="input-field text-sm py-1"
                  >
                    <option value="all">{t('common.all_statuses')}</option>
                    <option value="pending">{t('shipments.pending')}</option>
                    <option value="in_transit">{t('shipments.in_transit')}</option>
                    <option value="delivered">{t('shipments.delivered')}</option>
                    <option value="canceled">{t('shipments.canceled')}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="card-body">
              {filteredTrips.length === 0 ? (
                <div className="text-center py-12">
                  <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {statusFilter === 'all' 
                      ? t('vehicles.no_trips_found', 'No trips found') 
                      : t('vehicles.no_trips_with_status', 'No trips found with this status')
                    }
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {t('vehicles.trips_will_appear_here', 'Trips assigned to this vehicle will appear here')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTrips.map((trip) => (
                    <div
                      key={trip.id}
                      onClick={() => navigate(`/shipments/${trip.id}`)}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 rtl:space-x-reverse mb-2">
                            <h4 className="text-base font-semibold text-gray-900">
                              {trip.trackingNumber}
                            </h4>
                            {getStatusBadge(trip.status)}
                          </div>
                          
                          <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-600 mb-2">
                            <div className="flex items-center">
                              <MapPinIcon className="w-4 h-4 mr-1 rtl:ml-1 rtl:mr-0" />
                              <span>{trip.origin || trip.fromLocation} → {trip.destination || trip.toLocation}</span>
                            </div>
                            <div className="flex items-center">
                              <CalendarIcon className="w-4 h-4 mr-1 rtl:ml-1 rtl:mr-0" />
                              <span>{formatDate(trip.createdAt)}</span>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-500">
                            <span>{t('shipments.client')}: {trip.clientName}</span>
                            {trip.cost && (
                              <span>{t('shipments.cost')}: SAR {parseFloat(trip.cost).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {/* Removed view details button text */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Odometer Reading Modal */}
      <OdometerReadingModal
        isOpen={showOdometerModal}
        onClose={() => setShowOdometerModal(false)}
        vehicle={vehicle}
        onReadingAdded={handleOdometerReadingAdded}
      />

      {/* Odometer History Modal */}
      <OdometerHistoryModal
        isOpen={showOdometerHistory}
        onClose={() => setShowOdometerHistory(false)}
        vehicle={vehicle}
      />
    </div>
  );
};

export default VehicleDetail;